import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Save, Link2, Tag, Loader2 } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface VariantProduct {
  id_product: number
  reference: string | null
  name: string | null
  image_url: string | null
  label: string
}

interface VariantGroup {
  id_nxtal_variants: number
  products: VariantProduct[]
}

export default function ProductVariantsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  // labels draft por grupo: { [groupId]: { [productId]: "texto" } }
  const [drafts, setDrafts] = useState<Record<number, Record<number, string>>>({})

  const searchQuery = useQuery({
    queryKey: ['variant-groups-search', query],
    queryFn: async (): Promise<{ groups: VariantGroup[]; total: number }> => {
      if (!query) return { groups: [], total: 0 }
      const r = await api.get('/prestashop/variants/search', { params: { q: query, limit: 20 } })
      return r.data
    },
    enabled: query.length > 0,
  })

  const saveMutation = useMutation({
    mutationFn: async ({ groupId, labels }: { groupId: number; labels: Record<string, string> }) => {
      const r = await api.patch(`/prestashop/variants/${groupId}/labels`, { labels })
      return r.data as VariantGroup
    },
    onSuccess: (updated) => {
      toast.success(`Etiquetas guardadas en grupo #${updated.id_nxtal_variants}`)
      // limpiar draft de ese grupo y refrescar lista
      setDrafts((d) => {
        const n = { ...d }
        delete n[updated.id_nxtal_variants]
        return n
      })
      queryClient.invalidateQueries({ queryKey: ['variant-groups-search'] })
    },
    onError: (err: any) => {
      toast.error('Error al guardar', err.response?.data?.detail || err.message)
    },
  })

  const handleSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setQuery(searchInput.trim())
  }

  const setLabel = (groupId: number, productId: number, value: string) => {
    setDrafts((d) => ({
      ...d,
      [groupId]: { ...(d[groupId] || {}), [productId]: value },
    }))
  }

  const getDraftOrCurrent = (group: VariantGroup, productId: number, currentLabel: string): string => {
    const d = drafts[group.id_nxtal_variants]
    if (d && productId in d) return d[productId]
    return currentLabel
  }

  const isDirty = (groupId: number): boolean => {
    const d = drafts[groupId]
    return !!d && Object.keys(d).length > 0
  }

  const saveGroup = (group: VariantGroup) => {
    // Construir mapa final de labels (existing overlay con draft)
    const merged: Record<string, string> = {}
    for (const p of group.products) {
      const draftVal = drafts[group.id_nxtal_variants]?.[p.id_product]
      const val = draftVal !== undefined ? draftVal : p.label
      if (val && val.trim()) merged[String(p.id_product)] = val.trim()
    }
    saveMutation.mutate({ groupId: group.id_nxtal_variants, labels: merged })
  }

  const groups = searchQuery.data?.groups || []

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link2 className="w-7 h-7 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Etiquetas de variantes</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Edita el texto que aparece bajo la miniatura de cada variante en la ficha de producto.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmitSearch} className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="ID de producto, referencia (p.ej. PANT-119) o ID de grupo"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
        >
          <Search className="w-4 h-4" />
          Buscar
        </button>
      </form>

      {searchQuery.isLoading && (
        <div className="text-center py-10 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Buscando…
        </div>
      )}

      {!searchQuery.isLoading && query && groups.length === 0 && (
        <div className="text-center py-10 text-slate-500">
          Ningún grupo contiene productos que coincidan con <strong>{query}</strong>.
        </div>
      )}

      {!query && !searchQuery.isLoading && (
        <div className="text-center py-10 text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          Busca por ID de producto, referencia o ID de grupo para empezar.
        </div>
      )}

      <div className="space-y-6">
        {groups.map((group) => (
          <div
            key={group.id_nxtal_variants}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm"
          >
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded">
                  Grupo #{group.id_nxtal_variants}
                </span>
                <span className="text-xs text-slate-500">
                  {group.products.length} {group.products.length === 1 ? 'producto' : 'productos'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => saveGroup(group)}
                disabled={!isDirty(group.id_nxtal_variants) || saveMutation.isPending}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isDirty(group.id_nxtal_variants)
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {group.products.map((p) => {
                const current = getDraftOrCurrent(group, p.id_product, p.label)
                const changed = current !== p.label
                return (
                  <div key={p.id_product} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-14 h-14 flex-shrink-0 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name || ''} className="max-w-full max-h-full" />
                      ) : (
                        <Tag className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <a
                          href={`https://www.jvs-informatica.com/?id_product=${p.id_product}&controller=product`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-xs text-primary-600 hover:underline"
                        >
                          ID {p.id_product}
                        </a>
                        {p.reference && (
                          <span className="font-mono text-xs text-slate-500">
                            {p.reference}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-700 dark:text-slate-200 truncate">
                        {p.name || '(sin nombre)'}
                      </div>
                    </div>
                    <div className="w-64 flex-shrink-0">
                      <input
                        type="text"
                        placeholder="Sin etiqueta"
                        value={current}
                        onChange={(e) => setLabel(group.id_nxtal_variants, p.id_product, e.target.value)}
                        className={`w-full px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white ${
                          changed
                            ? 'border-amber-400 dark:border-amber-500 ring-1 ring-amber-200 dark:ring-amber-900/40'
                            : 'border-slate-200 dark:border-slate-600'
                        }`}
                        maxLength={120}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
